import { useCallback, useEffect, useMemo } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@/assets/styles/colors';
import { useAreas } from '@/api/domains/catalog/areas';
import {
  positionList,
  type PositionListItem,
  type PositionStatusFilter,
} from '@/api/domains/organization/positions';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import type { TableAction, TableColumn } from '@/components/ui/tables/Table';
import { Text } from '@/components/ui/text/Text';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { useUI } from '@/hooks/ui/useUI';
import { SECTIONS } from '@/types/sections';
import { formatToDdMmYyyyHHmm } from '@/utils';
import type { PositionFormRecord } from './usePositionFormPanel';
import { usePositionEnableDisable } from './usePositionEnableDisable';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';


interface PositionsFiltersState {
  status: PositionStatusFilter;
  areaId?: number;
}

interface UsePositionsListResult {
  rows: PositionListItem[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: PositionsFiltersState;
  setFilters: Dispatch<SetStateAction<PositionsFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<PositionListItem>[];
  actions: TableAction<PositionListItem>[];
  onSearch: (term: string) => void;
  openCreatePanel: () => void;
  loading: boolean;
  refetch: () => void;
}

export function usePositionsList(): UsePositionsListResult {
  const { t: translate } = useTranslation();
  const { openSidePanel, showApiError } = useUI();
  const { canSection } = usePagePermissions();
  const canCreatePositions = canSection(SECTIONS.CARGOS, 'create');
  const canEditPositions = canSection(SECTIONS.CARGOS, 'edit');
  const canTogglePositions = canSection(SECTIONS.CARGOS, 'delete');
  const {
    enablePosition,
    disablePosition,
    enablingIds,
    disablingIds,
  } = usePositionEnableDisable();
  const { areas } = useAreas();

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<PositionsFiltersState>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<PositionListItem> | null> => {
      const response = await positionList.list({
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        status: params.filters.status,
        areaId: params.filters.areaId,
      });
      if (!response.success || !response.data) {
        showApiError(response, translate('organization:messages.positionsLoadError'));
        return null;
      }
      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    filters,
    setFilters,
    refetch,
  } = usePaginatedList<PositionListItem, PositionsFiltersState>({
    fetchFn,
    initialFilters: { status: 'all' as PositionStatusFilter, areaId: undefined },
  });

  const togglePositionStatus = useCallback(async (row: PositionListItem) => {
    const success = row.isEnabled
      ? await disablePosition(row.id)
      : await enablePosition(row.id);

    if (success) {
      refetch();
    }
  }, [disablePosition, enablePosition, refetch]);

  useEffect(() => {
    const handlePositionFormSuccess = () => { refetch(); };
    window.addEventListener('position-form-success', handlePositionFormSuccess);
    return () => {
      window.removeEventListener('position-form-success', handlePositionFormSuccess);
    };
  }, [refetch]);

  const openPositionPanel = useCallback((record: PositionFormRecord, title: string) => {
    openSidePanel({
      title,
      component: 'PositionFormPanel',
      props: { record },
    });
  }, [openSidePanel]);

  const openCreatePanel = useCallback(() => {
    if (!canCreatePositions) {
      return;
    }

    openPositionPanel(
      { mode: 'create' },
      translate('organization:panel.createPositionTitle'),
    );
  }, [canCreatePositions, openPositionPanel, translate]);

  const openEditPanel = useCallback((row: PositionListItem) => {
    if (!canEditPositions) {
      return;
    }

    openPositionPanel(
      { mode: 'edit', positionId: row.id },
      translate('organization:panel.editPositionTitle'),
    );
  }, [canEditPositions, openPositionPanel, translate]);

  const filterConfig: SimpleFilter[] = useMemo(() => [
    {
      key: 'status',
      label: translate('organization:filters.status'),
      options: [
        { value: 'all', label: translate('organization:filters.all') },
        { value: 'enabled', label: translate('organization:filters.enabled') },
        { value: 'disabled', label: translate('organization:filters.disabled') },
      ],
    },
    {
      key: 'areaId',
      label: translate('organization:fields.area'),
      options: [
        { value: 'all', label: translate('organization:filters.allAreas') },
        ...areas.map((area) => ({
          value: String(area.id),
          label: area.name,
        })),
      ],
      placeholder: translate('organization:filters.allAreas'),
    },
  ], [areas, translate]);

  const columns: TableColumn<PositionListItem>[] = useMemo(
    () => [
      {
        key: 'id',
        header: translate('organization:fields.id'),
        sortable: true,
        render: (row: PositionListItem) => row.id,
        width: 80,
      },
      {
        key: 'code',
        header: translate('organization:fields.positionCode'),
        sortable: true,
        render: (row: PositionListItem) => row.code,
      },
      {
        key: 'name',
        header: translate('organization:fields.positionName'),
        sortable: true,
        render: (row: PositionListItem) => row.name,
      },
      {
        key: 'description',
        header: translate('organization:fields.description'),
        sortable: true,
        render: (row: PositionListItem) => row.description,
      },
      {
        key: 'areaName',
        header: translate('organization:fields.area'),
        sortable: true,
        render: (row: PositionListItem) => row.areaName,
      },
      {
        key: 'modifiedDate',
        header: translate('common:fields.modifiedDate'),
        sortable: true,
        render: (row: PositionListItem) => formatToDdMmYyyyHHmm(row.modifiedDate) || '-',
      },
      {
        key: 'status',
        header: translate('organization:fields.status'),
        sortable: true,
        render: (row: PositionListItem) => (
          <Text
            variant="body-regular"
            style={{
              color: row.isEnabled ? colors.feedback.success400 : colors.grays.neutral99,
              fontWeight: 500,
            }}
          >
            {row.isEnabled
              ? translate('organization:status.enabled')
              : translate('organization:status.disabled')}
          </Text>
        ),
      },
    ],
    [translate],
  );

  const actions: TableAction<PositionListItem>[] = useMemo(
    () => [
      {
        id: 'edit',
        label: translate('common:actions.edit'),
        icon: 'edit',
        hidden: () => !canEditPositions,
        onClick: (row: PositionListItem) => {
          openEditPanel(row);
        },
      },
      {
        id: 'enable',
        label: translate('organization:actions.enablePosition'),
        icon: 'check',
        hidden: (row: PositionListItem) => !canTogglePositions || row.isEnabled,
        disabled: (row: PositionListItem) => enablingIds.has(row.id) || disablingIds.has(row.id),
        isLoading: (row: PositionListItem) => enablingIds.has(row.id) || disablingIds.has(row.id),
        color: colors.feedback.success400,
        onClick: (row: PositionListItem) => {
          void togglePositionStatus(row);
        },
      },
      {
        id: 'disable',
        label: translate('organization:actions.disablePosition'),
        icon: 'close',
        hidden: (row: PositionListItem) => !canTogglePositions || !row.isEnabled,
        disabled: (row: PositionListItem) => enablingIds.has(row.id) || disablingIds.has(row.id),
        isLoading: (row: PositionListItem) => enablingIds.has(row.id) || disablingIds.has(row.id),
        color: colors.feedback.error400,
        onClick: (row: PositionListItem) => {
          void togglePositionStatus(row);
        },
      },
    ],
    [
      canEditPositions,
      canTogglePositions,
      disablingIds,
      enablingIds,
      openEditPanel,
      togglePositionStatus,
      translate,
    ],
  );

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    openCreatePanel,
    loading,
    refetch,
  };
}

export default usePositionsList;
